/**
 * Client-side known-route manifest for the web SPA shell.
 *
 * Root `_layout.jsx` uses this to show a real 404 for garbage URLs instead of
 * rendering the app shell with a misleading 200. Dynamic segments mirror the
 * Expo app tree under `frontend/app/`.
 */

const STATIC_ROUTES = new Set([
  '/',
  '/app',
  '/affiliate',
  '/about',
  '/brand',
  '/pricing',
  '/business',
  '/contact',
  '/feedback',
  '/privacy-policy',
  '/terms-of-service',
  '/affiliate-program-agreement',
  '/terms-of-sale',
  '/refund-policy',
  '/ccpa',
  '/dmca',
  '/guidelines',
  '/enterprise',
  '/features',
  '/download',
  '/mac',
  '/extension',
  '/voice',
  '/free-ai-chat',
  '/alternatives',
  '/chatgpt-alternative',
  '/poe-alternative',
  '/monica-alternative',
  '/chathub-alternative',
  '/typingmind-alternative',
  '/t3chat-alternative',
  '/galaxy-ai-alternative',
  '/openrouter-alternative',
  '/right-wing-ai',
  '/based-benchmark',
  '/changelog',
  '/llms',
  '/api',
  '/switch',
  '/login',
  '/login-authorize',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/onboarding',
  '/paywall',
  '/payment-successful',
  '/auto-topup-setup-complete',
  '/success',
  '/impersonate',
  '/blog',
  '/blog/rss.xml',
  '/docs',
  '/docs/api',
  '/docs/api-auth',
  '/docs/api-endpoints',
  '/docs/quickstart',
  '/docs/chat',
  '/docs/images',
  '/docs/videos',
  '/docs/music',
  '/docs/voice',
  '/docs/memory',
  '/docs/files',
  '/docs/editors',
  '/docs/collections',
  '/docs/agents',
  '/docs/tools',
  '/docs/tasks',
  '/docs/bookmarks',
  '/docs/credits',
  '/docs/pricing-guide',
  '/docs/overview',
  '/docs/privacy',
  '/docs/import-chats',
  '/docs/data-export',
  '/docs/web-search',
  '/docs/opencode',
  '/docs/openclaw',
  '/docs/cursor-ide',
  '/docs/claude-code',
  '/docs/codex-cli',
  '/docs/pi-cli',
  '/docs/hermes',
  '/docs/prime-agent',
  '/learn',
  '/settings',
  '/remote-projects',
  '/referral',
  '/memory',
  '/ideas',
  '/maps',
  '/bookmarks',
  '/sites',
  '/sites/report',
  '/diagnostics',
  '/deck',
  '/local-deck',
  '/local-work',
  '/collections-lab',
  '/memory-mock',
  '/agents',
  '/agents/search',
  '/explore',
  '/explore/categories',
  '/topics',
  '/tags',
  '/prompts',
  '/tools',
  '/plugins',
  '/tools/history',
  '/tasks',
  '/tasks/browse',
  '/coworker',
  '/coworker/schedules',
  '/images',
  '/videos',
  '/music',
  '/files',
  '/files/search',
  '/library',
  '/collections',
  '/models',
  '/decks/recipes',
  '/use-cases',
]);

const DYNAMIC_ROUTE_PATTERNS = [
  /^\/blog\/category\/[^/]+$/,
  /^\/blog\/[^/]+$/,
  /^\/learn\/[^/]+$/,
  /^\/docs\/[^/]+$/,
  /^\/docs\/learn\/[^/]+$/,
  /^\/start\/[^/]+$/,
  /^\/topics\/category\/[^/]+$/,
  /^\/tags\/[^/]+$/,
  /^\/prompts\/category\/[^/]+$/,
  /^\/prompts\/[^/]+$/,
  /^\/agents\/category\/[^/]+$/,
  /^\/agents\/[^/]+$/,
  /^\/explore\/category\/[^/]+$/,
  /^\/explore\/item\/[^/]+$/,
  /^\/explore\/[^/]+$/,
  /^\/tools\/category\/[^/]+$/,
  /^\/tools\/[^/]+$/,
  /^\/coworker\/[^/]+$/,
  /^\/workspace\/[^/]+$/,
  /^\/workspace\/[^/]+\/c\/[^/]+$/,
  /^\/workspace\/[^/]+\/work$/,
  /^\/workspace\/[^/]+\/work\/[^/]+$/,
  /^\/tasks\/category\/[^/]+$/,
  /^\/tasks\/template\/[^/]+$/,
  /^\/tasks\/[^/]+$/,
  /^\/tasks\/[^/]+\/email-settings$/,
  /^\/decks\/recipes\/category\/[^/]+$/,
  /^\/decks\/recipes\/[^/]+$/,
  /^\/use-cases\/[^/]+$/,
  /^\/collections\/[^/]+$/,
  /^\/c\/[^/]+$/,
  /^\/m\/[^/]+$/,
  /^\/g\/[^/]+$/,
];

function normalizePathname(pathname) {
  const raw = String(pathname || '').split('?')[0].split('#')[0] || '/';
  if (raw === '/') return '/';
  return raw.replace(/\/+$/, '') || '/';
}

/** True when `pathname` maps to a real Expo route (not a junk URL). */
export function isKnownSpaRoute(pathname) {
  const path = normalizePathname(pathname);
  if (STATIC_ROUTES.has(path)) return true;
  return DYNAMIC_ROUTE_PATTERNS.some((re) => re.test(path));
}
